import { Box, MenuItem, MenuList, Popover, Stack, Typography } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { VyIcon } from '@vy/ui-design-system';
import { isNotNullOrUndefined, vyDate, type VyDate } from '@vy/utils';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  computePresetRange,
  DATE_PRESET_KEYS,
  DATE_PRESET_OPTIONS,
  encodeDatePresetValue,
  parseDatePresetValue,
  type DatePresetKey,
} from '../date-preset-consts';

type DatePresetValueInputProps = {
  value: string;
  disabled: boolean;
  onValueChange: (value: string) => void;
};

const COMPACT_FORMAT = 'MM/DD/YY';

export const DatePresetValueInput: React.FC<DatePresetValueInputProps> = React.memo(
  ({ value, disabled, onValueChange }) => {
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const parsed = useMemo(() => parseDatePresetValue(value), [value]);

    const isCustomSelected = parsed?.preset === DATE_PRESET_KEYS.Custom;

    const displayLabel = useMemo(() => {
      if (!parsed) {
        return 'Select...';
      }

      if (parsed.preset === DATE_PRESET_KEYS.Custom) {
        if (parsed.from && parsed.to) {
          return `${vyDate(parsed.from).format(COMPACT_FORMAT)} – ${vyDate(parsed.to).format(COMPACT_FORMAT)}`;
        }
        return 'Custom Range';
      }

      const match = DATE_PRESET_OPTIONS.find((option) => option.value === parsed.preset);
      return match?.label ?? 'Select...';
    }, [parsed]);

    const handleOpen = useCallback(() => {
      if (disabled) return;
      setIsOpen(true);
    }, [disabled]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleSelectPreset = useCallback(
      (nextPreset: DatePresetKey) => {
        if (nextPreset === DATE_PRESET_KEYS.Custom) {
          if (parsed?.preset !== DATE_PRESET_KEYS.Custom) {
            onValueChange(`${DATE_PRESET_KEYS.Custom}:`);
          }
          return;
        }

        const resolved = computePresetRange(nextPreset);

        if (resolved) {
          onValueChange(encodeDatePresetValue(nextPreset, resolved.from, resolved.to));
        }

        setIsOpen(false);
      },
      [parsed?.preset, onValueChange],
    );

    const calendarValue: DateRange<VyDate> = useMemo(() => {
      if (parsed?.from && parsed?.to) {
        return [vyDate(parsed.from), vyDate(parsed.to)];
      }
      return [null, null];
    }, [parsed?.from, parsed?.to]);

    const handleCalendarAccept = useCallback(
      (newRange: DateRange<VyDate>) => {
        const [startDayjs, endDayjs] = newRange;

        if (!isNotNullOrUndefined(startDayjs) || !isNotNullOrUndefined(endDayjs)) {
          return;
        }

        const start = vyDate(startDayjs, { keepLocalTime: true }).startOf('day').toDate();
        const end = vyDate(endDayjs, { keepLocalTime: true }).startOf('day').toDate();

        onValueChange(encodeDatePresetValue(DATE_PRESET_KEYS.Custom, start, end));
        setIsOpen(false);
      },
      [onValueChange],
    );

    const hasValue = isNotNullOrUndefined(parsed);

    return (
      <>
        <Box
          ref={triggerRef}
          onClick={handleOpen}
          sx={{
            width: '170px',
            height: '36px',
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            backgroundColor: 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            fontSize: '14px',
            color: hasValue ? 'inherit' : '#0d1b27de',
            opacity: disabled ? 0.5 : 1,
            '&:hover': {
              borderColor: disabled ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.87)',
            },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayLabel}
          </Box>
          <VyIcon icon="ArrowDown" style={{ transform: 'scale(0.75)' }} />
        </Box>
        <Popover
          open={isOpen}
          anchorEl={triggerRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                borderRadius: '8px',
                boxShadow: '0px 8px 16px 0px #0C124514',
                border: '1px solid #F0F4F6',
              },
            },
          }}
        >
          <Stack direction="row" sx={{
            alignItems: "stretch"
          }}>
            <MenuList
              sx={{
                py: 1,
                minWidth: 170,
                borderRight: isCustomSelected ? '1px solid #F0F4F6' : 'none',
              }}
            >
              {DATE_PRESET_OPTIONS.map((option) => {
                const isSelected = parsed?.preset === option.value;

                return (
                  <MenuItem
                    key={option.value}
                    selected={isSelected}
                    onClick={() => handleSelectPreset(option.value)}
                    sx={{ fontSize: 14, py: 0.75 }}
                  >
                    {option.label}
                  </MenuItem>
                );
              })}
            </MenuList>

            {isCustomSelected && (
              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    px: 1,
                    pb: 0.5
                  }}>
                  Pick a custom range
                </Typography>
                <StaticDateRangePicker
                  value={calendarValue}
                  onAccept={handleCalendarAccept}
                  calendars={2}
                  slotProps={{
                    actionBar: { actions: ['cancel', 'accept'] },
                    toolbar: { hidden: true },
                  }}
                  sx={{
                    '& .MuiPickersLayout-root': { minWidth: 'auto' },
                  }}
                />
              </Box>
            )}
          </Stack>
        </Popover>
      </>
    );
  },
);
